

export const dynamic = 'force-dynamic';

import { useState, useEffect } from "react";
import VendorReportsLayout from "../VendorReportsLayout";
import { reportsApi, vendorsApi, mastersApi } from "@/lib/api";

// ─── Types ────────────────────────────────────────────────────────────────────

interface VendorComparison {
  id: number;
  name: string;
  contact_person?: string;
  phone?: string;
  email?: string;
  gst_number?: string;
  vendor_type?: string;
  commission_type?: string;
  commission_value: number;
  total_events: number;
  completed_events: number;
  cancelled_events: number;
  completion_rate: number;
  cancellation_rate: number;
  total_billed: number;
  total_paid: number;
  current_balance: number;
  services_count: number;
  avg_base_price: number;
  avg_selling_price: number;
  reliability_score: number;
}

interface VendorOption {
  id: number;
  name: string;
  vendor_type_id?: number;
  vendor_type?: { name: string };
}

interface VendorTypeOption {
  id: number;
  name: string;
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

const fmt = (n: number) =>
  new Intl.NumberFormat("en-IN", { style: "currency", currency: "INR", maximumFractionDigits: 0 }).format(n ?? 0);

const highlightBest = (values: number[], lowerIsBetter: boolean = false) => {
  if (values.length === 0) return -1;
  const best = lowerIsBetter ? Math.min(...values) : Math.max(...values);
  return values.indexOf(best);
};

// ─── Page Component ───────────────────────────────────────────────────────────

export default function VendorComparisonPage() {
  const [vendors, setVendors] = useState<VendorComparison[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedVendors, setSelectedVendors] = useState<number[]>([]);
  const [vendorType, setVendorType] = useState<number | undefined>(undefined);
  const [allVendors, setAllVendors] = useState<VendorOption[]>([]);
  const [vendorTypes, setVendorTypes] = useState<VendorTypeOption[]>([]);

  // Load vendors and vendor types
  useEffect(() => {
    vendorsApi.list().then((res: any) => {
      if (res?.success) {
        const data = res.data;
        setAllVendors(Array.isArray(data) ? data : (data?.data || []));
      }
    });

    mastersApi.vendorTypes.list().then((res: any) => {
      if (res?.success) {
        const data = res.data;
        setVendorTypes(Array.isArray(data) ? data : (data?.data || []));
      }
    });
  }, []);

  // Load comparison data
  const handleCompare = () => {
    if (selectedVendors.length === 0 && !vendorType) {
      alert("Please select vendors or a vendor type to compare");
      return;
    }

    setLoading(true);
    const params: any = {};
    if (selectedVendors.length > 0) {
      params.vendor_ids = selectedVendors;
    }
    if (vendorType) {
      params.vendor_type_id = vendorType;
    }

    reportsApi
      .vendorComparison(params)
      .then((res: any) => {
        if (res?.success) {
          setVendors(res.data.vendors || []);
        }
      })
      .catch((err) => console.error("Failed to load vendor comparison:", err))
      .finally(() => setLoading(false));
  };

  const toggleVendor = (vendorId: number) => {
    setSelectedVendors((prev) =>
      prev.includes(vendorId) ? prev.filter((id) => id !== vendorId) : [...prev, vendorId]
    );
  };

  return (
    <VendorReportsLayout>
      {/* ── Selection Panel ── */}
      <div className="bg-white rounded-2xl border border-stone-200 p-5">
        <h3 className="font-bold text-stone-800 mb-4 font-serif text-sm">Select Vendors to Compare</h3>
        
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
          {/* Vendor Type Filter */}
          <div>
            <label className="block text-xs font-medium text-stone-600 mb-1">Filter by Vendor Type</label>
            <select
              value={vendorType || ""}
              onChange={(e) => setVendorType(e.target.value ? Number(e.target.value) : undefined)}
              className="w-full px-3 py-2 text-sm rounded-lg border border-stone-200 focus:border-amber-400 outline-none"
            >
              <option value="">All Types</option>
              {vendorTypes.map((vt) => (
                <option key={vt.id} value={vt.id}>{vt.name}</option>
              ))}
            </select>
          </div>

          {/* Action Button */}
          <div className="flex items-end">
            <button
              onClick={handleCompare}
              disabled={loading || (selectedVendors.length === 0 && !vendorType)}
              className="w-full px-4 py-2 rounded-xl text-white font-semibold text-sm gold-gradient hover:opacity-90 transition-opacity disabled:opacity-50"
            >
              {loading ? "Loading..." : "Compare Vendors"}
            </button>
          </div>
        </div>

        {/* Vendor Selection */}
        <div className="flex flex-wrap gap-2">
          {allVendors
            .filter((v) => !vendorType || v.vendor_type_id === vendorType)
            .map((vendor) => (
              <button
                key={vendor.id}
                onClick={() => toggleVendor(vendor.id)}
                className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors border ${
                  selectedVendors.includes(vendor.id)
                    ? "bg-amber-50 border-amber-400 text-amber-700"
                    : "bg-white border-stone-200 text-stone-600 hover:border-stone-300"
                }`}
              >
                {vendor.name}
                {selectedVendors.includes(vendor.id) && " ✓"}
              </button>
            ))}
        </div>
      </div>

      {/* ── Comparison Table ── */}
      {vendors.length > 0 && (
        <div className="bg-white rounded-2xl border border-stone-200 overflow-hidden">
          <div className="px-5 py-4 border-b border-stone-100">
            <h3 className="font-bold text-stone-800 font-serif text-sm">Vendor Comparison Results</h3>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-stone-100 bg-stone-50">
                  <th className="text-left px-5 py-3 text-xs font-semibold text-stone-500 uppercase">Metric</th>
                  {vendors.map((vendor) => (
                    <th key={vendor.id} className="text-center px-4 py-3 text-xs font-semibold text-stone-700">
                      {vendor.name}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-stone-50">
                {/* Basic Info */}
                <tr className="bg-white">
                  <td className="px-5 py-3 font-medium text-stone-700">Vendor Type</td>
                  {vendors.map((v, idx) => (
                    <td key={idx} className="px-4 py-3 text-center text-stone-600">{v.vendor_type || "-"}</td>
                  ))}
                </tr>
                <tr className="bg-white">
                  <td className="px-5 py-3 font-medium text-stone-700">Contact Person</td>
                  {vendors.map((v, idx) => (
                    <td key={idx} className="px-4 py-3 text-center text-stone-600">{v.contact_person || "-"}</td>
                  ))}
                </tr>
                <tr className="bg-white">
                  <td className="px-5 py-3 font-medium text-stone-700">Phone</td>
                  {vendors.map((v, idx) => (
                    <td key={idx} className="px-4 py-3 text-center text-stone-600">{v.phone || "-"}</td>
                  ))}
                </tr>

                {/* Performance Metrics */}
                <tr className="bg-stone-50">
                  <td className="px-5 py-3 font-semibold text-stone-800" colSpan={vendors.length + 1}>Performance</td>
                </tr>
                {(() => {
                  const eventValues = vendors.map((v) => v.total_events);
                  const bestIdx = highlightBest(eventValues);
                  return (
                    <tr className="bg-white">
                      <td className="px-5 py-3 font-medium text-stone-700">Total Events</td>
                      {vendors.map((v, idx) => (
                        <td key={idx} className={`px-4 py-3 text-center font-semibold ${idx === bestIdx ? "text-emerald-700" : "text-stone-700"}`}>
                          {v.total_events}
                          {idx === bestIdx && " ⭐"}
                        </td>
                      ))}
                    </tr>
                  );
                })()}
                {(() => {
                  const values = vendors.map((v) => v.completion_rate);
                  const bestIdx = highlightBest(values);
                  return (
                    <tr className="bg-white">
                      <td className="px-5 py-3 font-medium text-stone-700">Completion Rate</td>
                      {vendors.map((v, idx) => (
                        <td key={idx} className={`px-4 py-3 text-center font-semibold ${idx === bestIdx ? "text-emerald-700" : "text-stone-700"}`}>
                          {v.completion_rate.toFixed(1)}%
                          {idx === bestIdx && " ⭐"}
                        </td>
                      ))}
                    </tr>
                  );
                })()}
                {(() => {
                  const values = vendors.map((v) => v.reliability_score);
                  const bestIdx = highlightBest(values);
                  return (
                    <tr className="bg-white">
                      <td className="px-5 py-3 font-medium text-stone-700">Reliability Score</td>
                      {vendors.map((v, idx) => (
                        <td key={idx} className={`px-4 py-3 text-center font-semibold ${idx === bestIdx ? "text-emerald-700" : "text-stone-700"}`}>
                          {v.reliability_score.toFixed(1)}%
                          {idx === bestIdx && " ⭐"}
                        </td>
                      ))}
                    </tr>
                  );
                })()}
                {(() => {
                  const values = vendors.map((v) => v.cancellation_rate);
                  const bestIdx = highlightBest(values, true);
                  return (
                    <tr className="bg-white">
                      <td className="px-5 py-3 font-medium text-stone-700">Cancellation Rate</td>
                      {vendors.map((v, idx) => (
                        <td key={idx} className={`px-4 py-3 text-center font-semibold ${idx === bestIdx ? "text-emerald-700" : "text-rose-600"}`}>
                          {v.cancellation_rate.toFixed(1)}%
                          {idx === bestIdx && " ⭐"}
                        </td>
                      ))}
                    </tr>
                  );
                })()}

                {/* Financial Metrics */}
                <tr className="bg-stone-50">
                  <td className="px-5 py-3 font-semibold text-stone-800" colSpan={vendors.length + 1}>Financial</td>
                </tr>
                {(() => {
                  const values = vendors.map((v) => v.total_billed);
                  const bestIdx = highlightBest(values);
                  return (
                    <tr className="bg-white">
                      <td className="px-5 py-3 font-medium text-stone-700">Total Billed</td>
                      {vendors.map((v, idx) => (
                        <td key={idx} className={`px-4 py-3 text-center font-semibold ${idx === bestIdx ? "text-emerald-700" : "text-stone-700"}`}>
                          {fmt(v.total_billed)}
                          {idx === bestIdx && " ⭐"}
                        </td>
                      ))}
                    </tr>
                  );
                })()}
                <tr className="bg-white">
                  <td className="px-5 py-3 font-medium text-stone-700">Commission Type</td>
                  {vendors.map((v, idx) => (
                    <td key={idx} className="px-4 py-3 text-center text-stone-600">
                      {v.commission_type === 'percentage' ? `${v.commission_value}%` : fmt(v.commission_value)}
                    </td>
                  ))}
                </tr>
                <tr className="bg-white">
                  <td className="px-5 py-3 font-medium text-stone-700">Current Balance</td>
                  {vendors.map((v, idx) => (
                    <td key={idx} className={`px-4 py-3 text-center font-semibold ${v.current_balance > 0 ? "text-amber-600" : "text-emerald-600"}`}>
                      {fmt(v.current_balance)}
                    </td>
                  ))}
                </tr>

                {/* Services */}
                <tr className="bg-stone-50">
                  <td className="px-5 py-3 font-semibold text-stone-800" colSpan={vendors.length + 1}>Services</td>
                </tr>
                {(() => {
                  const values = vendors.map((v) => v.services_count);
                  const bestIdx = highlightBest(values);
                  return (
                    <tr className="bg-white">
                      <td className="px-5 py-3 font-medium text-stone-700">Services Offered</td>
                      {vendors.map((v, idx) => (
                        <td key={idx} className={`px-4 py-3 text-center font-semibold ${idx === bestIdx ? "text-emerald-700" : "text-stone-700"}`}>
                          {v.services_count}
                          {idx === bestIdx && " ⭐"}
                        </td>
                      ))}
                    </tr>
                  );
                })()}
                <tr className="bg-white">
                  <td className="px-5 py-3 font-medium text-stone-700">Avg Base Price</td>
                  {vendors.map((v, idx) => (
                    <td key={idx} className="px-4 py-3 text-center text-stone-600">{fmt(v.avg_base_price)}</td>
                  ))}
                </tr>
                <tr className="bg-white">
                  <td className="px-5 py-3 font-medium text-stone-700">Avg Selling Price</td>
                  {vendors.map((v, idx) => (
                    <td key={idx} className="px-4 py-3 text-center text-stone-600">{fmt(v.avg_selling_price)}</td>
                  ))}
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      )}
    </VendorReportsLayout>
  );
}
